import { Leaf, Recycle, Droplet, TreePine, Heart, Sun } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';

const Sustainability = () => {
  const { language, isRTL } = useLanguage();

  const initiatives = language === 'ar' ? [
    {
      icon: Leaf,
      title: 'الزراعة العضوية',
      description: 'نعطي الأولوية لأساليب الزراعة العضوية والمستدامة التي تحمي صحة التربة والتنوع البيولوجي.'
    },
    {
      icon: Recycle,
      title: 'إدارة النفايات',
      description: 'برامج شاملة للحد من النفايات وإعادة التدوير تقلل من بصمتنا البيئية.'
    },
    {
      icon: Droplet,
      title: 'الحفاظ على المياه',
      description: 'أنظمة إدارة المياه الفعالة تقلل الاستهلاك وتمنع التلوث.'
    },
    {
      icon: TreePine,
      title: 'الإشراف على الأراضي',
      description: 'ممارسات إدارة الأراضي المسؤولة تحافظ على النظم البيئية الطبيعية وتمنع التآكل.'
    },
    {
      icon: Sun,
      title: 'الطاقة المتجددة',
      description: 'الاستثمار في مصادر الطاقة المتجددة لتقليل انبعاثات الكربون وتكاليف الطاقة.'
    },
    {
      icon: Heart,
      title: 'رعاية الحيوان',
      description: 'معاملة أخلاقية وظروف إنسانية لجميع الماشية طوال حياتها.'
    }
  ] : [
    {
      icon: Leaf,
      title: 'Organic Farming',
      description: 'We prioritize organic and sustainable farming methods that protect soil health and biodiversity.'
    },
    {
      icon: Recycle,
      title: 'Waste Management',
      description: 'Comprehensive waste reduction and recycling programs minimize our environmental footprint.'
    },
    {
      icon: Droplet,
      title: 'Water Conservation',
      description: 'Efficient water management systems reduce consumption and prevent contamination.'
    },
    {
      icon: TreePine,
      title: 'Land Stewardship',
      description: 'Responsible land management practices preserve natural ecosystems and prevent erosion.'
    },
    {
      icon: Sun,
      title: 'Renewable Energy',
      description: 'Investing in renewable energy sources to reduce carbon emissions and energy costs.'
    },
    {
      icon: Heart,
      title: 'Animal Welfare',
      description: 'Ethical treatment and humane conditions for all livestock throughout their lives.'
    }
  ];

  return (
    <div className="min-h-screen" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero */}
      <section className="bg-stone-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">
            {language === 'ar' ? 'الاستدامة' : 'Sustainability'}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {language === 'ar'
              ? 'ملتزمون بالمسؤولية البيئية والممارسات الأخلاقية'
              : 'Committed to environmental responsibility and ethical practices'}
          </p>
        </div>
      </section>

      {/* Environmental Responsibility */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className={isRTL ? 'md:order-2' : ''}>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {language === 'ar' ? 'المسؤولية البيئية' : 'Environmental Responsibility'}
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {language === 'ar'
                  ? 'في إيليت ميت فارم، ندرك مسؤوليتنا في حماية والحفاظ على البيئة للأجيال القادمة. عملياتنا مصممة لتقليل التأثير البيئي مع الحفاظ على أعلى معايير الجودة.'
                  : 'At EliteMeat Farm, we recognize our responsibility to protect and preserve the environment for future generations. Our operations are designed to minimize environmental impact while maintaining the highest quality standards.'}
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {language === 'ar'
                  ? 'نستثمر باستمرار في الممارسات المستدامة، من الطاقة المتجددة إلى الحفاظ على المياه، لضمان أن أنشطتنا الزراعية تساهم بشكل إيجابي في النظام البيئي.'
                  : 'We continuously invest in sustainable practices, from renewable energy to water conservation, ensuring that our farming activities contribute positively to the ecosystem.'}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {language === 'ar'
                  ? 'التزامنا يتجاوز الامتثال - نحن نسعى بنشاط إلى حلول مبتكرة لتقليل بصمتنا الكربونية وتعزيز الاستدامة البيئية.'
                  : 'Our commitment extends beyond compliance—we actively seek innovative solutions to reduce our carbon footprint and promote environmental sustainability.'}
              </p>
            </div>
            <div className={isRTL ? 'md:order-1' : ''}>
              <img
                src="https://images.unsplash.com/photo-1472214103451-9374bd1c798e?w=800"
                alt={language === 'ar' ? 'الاستدامة' : 'Sustainability'}
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sustainability Initiatives */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'ar' ? 'مبادرات الاستدامة لدينا' : 'Our Sustainability Initiatives'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'ar'
                ? 'برامج شاملة للحفاظ على بيئتنا'
                : 'Comprehensive programs to preserve our environment'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {initiatives.map((initiative, index) => {
              const IconComponent = initiative.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <IconComponent className="text-green-600" size={28} />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{initiative.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{initiative.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Community Impact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className={isRTL ? '' : 'order-2'}>
              <img
                src="https://images.unsplash.com/photo-1560493676-04071c5f467b?w=800"
                alt={language === 'ar' ? 'تأثير المجتمع' : 'Community Impact'}
                className="rounded-lg shadow-xl"
              />
            </div>
            <div className={isRTL ? '' : 'order-1'}>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {language === 'ar' ? 'تأثير المجتمع' : 'Community Impact'}
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {language === 'ar'
                  ? 'جهود الاستدامة لدينا تمتد إلى ما وراء مزارعنا. ندعم المجتمعات المحلية من خلال توفير التوظيف وبرامج التدريب والمبادرات التعليمية.'
                  : 'Our sustainability efforts extend beyond our farms. We support local communities through employment, training programs, and educational initiatives.'}
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {language === 'ar'
                  ? 'نتشارك مع المزارعين المحليين ونوفر الموارد والمعرفة لمساعدتهم على تبني ممارسات مستدامة في عملياتهم الخاصة.'
                  : 'We partner with local farmers, providing resources and knowledge to help them adopt sustainable practices in their own operations.'}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {language === 'ar'
                  ? 'التزامنا بالاستدامة يخلق تأثيرًا إيجابيًا متموجًا، يفيد البيئة ومجتمعنا وصناعتنا.'
                  : 'Our commitment to sustainability creates a positive ripple effect, benefiting the environment, our community, and our industry.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            {language === 'ar' ? 'انضم إلى رحلة الاستدامة لدينا' : 'Join Our Sustainability Journey'}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {language === 'ar'
              ? 'معًا، يمكننا إحداث فرق. اختر منتجات مصدرها مسؤول ودعم الممارسات المستدامة.'
              : 'Together, we can make a difference. Choose responsibly sourced products and support sustainable practices.'}
          </p>
          <a 
            href="/products" 
            className="inline-block bg-white text-green-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            {language === 'ar' ? 'تسوق منتجاتنا' : 'Shop Our Products'}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Sustainability;
